import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApplyWrapper,
  Container,
  FilterTypeLabel,
  FilterTypesWrapper,
  Title,
  TitleContainer,
} from './EditCompanyDataModal.styled';
import {
  EditCompanyDataTab,
  IEditCompanyDataDTO,
  ICompanyDetails,
} from '../types';
import { DetailsTab } from './DetailsTab/DetailsTab';
import Button from 'components/Button/Button';
import { getInitialAddressData, getInitialDetailsData } from './helpers';
import { AddressTab } from './AddressTab/AddressTab';
import { useEditCompany } from './hooks';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { IAddress } from 'types/Address.types';
import { Modal } from 'components/Modals/Modal/Modal';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';

interface IEditCompanyDataModalProps {
  onCancel: () => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  data: any;
}

export const EditCompanyDataModal = ({
  onCancel,
  isOpen,
  setIsOpen,
  data,
}: IEditCompanyDataModalProps) => {
  const { t } = useTranslation();
  const { isSmallTablet } = useBreakpointFlag();

  const { company } = useSelector((state: IRootReducerState) => state.userInfo);

  const { mutate: editCompany } = useEditCompany(Number(company.id));

  const [selectedTab, setSelectedTab] = useState<EditCompanyDataTab>(
    EditCompanyDataTab.DETAILS
  );

  const [details, setDetails] = useState<ICompanyDetails>(
    getInitialDetailsData(data)
  );
  const [address, setAddress] = useState<IAddress>(getInitialAddressData(data));

  const [isValidDetailsTab, setIsValidDetailsTab] = useState<boolean>(true);
  const [isValidAddressTab, setIsValidAddressTab] = useState<boolean>(true);

  const handleOnClick = () => {
    const editCompanyDataDTO: IEditCompanyDataDTO = {
      name: details.name,
      vat_number: details.vat_number,
      coc_number: details.coc_number,
      website: details.website === '' ? null : details.website,
      email: details.email,
      phone_number: details.phone_number === '' ? null : details.phone_number,
      address: {
        ...address,
        house_number: address.house_number === '' ? null : address.house_number,
      },
      company_settings: data?.company_settings,
    };
    editCompany(editCompanyDataDTO, {
      onSuccess: () => setIsOpen(false),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      modalStyle={{
        position: 'fixed',
        margin: 'auto',
        width: isSmallTablet ? '90%' : '700rem',
        minWidth: isSmallTablet ? '90%' : 'auto',
      }}
    >
      <Container>
        <TitleContainer>
          <Title>{t('Edit company data')}</Title>
        </TitleContainer>
        <FilterTypesWrapper>
          <FilterTypeLabel
            onClick={() => setSelectedTab(EditCompanyDataTab.DETAILS)}
            isSelected={selectedTab === EditCompanyDataTab.DETAILS}
          >
            {t(EditCompanyDataTab.DETAILS)}
          </FilterTypeLabel>
          <FilterTypeLabel
            onClick={() => setSelectedTab(EditCompanyDataTab.ADDRESS)}
            isSelected={selectedTab === EditCompanyDataTab.ADDRESS}
          >
            {t(EditCompanyDataTab.ADDRESS)}
          </FilterTypeLabel>
        </FilterTypesWrapper>
        {selectedTab === EditCompanyDataTab.DETAILS && (
          <DetailsTab
            details={details}
            setDetails={setDetails}
            setIsValid={setIsValidDetailsTab}
          />
        )}
        {selectedTab === EditCompanyDataTab.ADDRESS && (
          <AddressTab
            address={address}
            setAddress={setAddress}
            setIsValid={setIsValidAddressTab}
          />
        )}
        <ApplyWrapper>
          <Button
            onClick={onCancel}
            secondary
            width="200rem"
            label={t('Cancel')}
          />
          <Button
            disabled={!isValidDetailsTab || !isValidAddressTab}
            onClick={() => {
              if (isValidDetailsTab && isValidAddressTab) {
                handleOnClick();
                onCancel();
              }
            }}
            primary
            width="200rem"
            label={t('Apply')}
          />
        </ApplyWrapper>
      </Container>
    </Modal>
  );
};
